import { initGatewayTypes } from '../shared/credit-card-gateway/credit-card-gateway.service';

/**
 * Pure model for the gateway resolution graph (no Cytoscape, no Angular).
 * normalizeGraph is the only function that sees the raw response; it reads a
 * fixed allow-list of fields and builds new literals, so nothing else (credentials
 * included) can reach node data.
 */

export type GraphNodeType = 'item' | 'accountingPackage' | 'paymentType' | 'gateway' | 'more';
export type GraphEdgeKind = 'belongsTo' | 'contains' | 'uses' | 'paidWith' | 'defaultsTo';
export type StartType = Exclude<GraphNodeType, 'more'>;
export interface GraphStart {
  type: StartType | 'invoice';
  id: number;
}

/** Per-type allow-listed node data. No other key may appear. */
export interface ItemNodeData {
  description: string | null;
  accountingPackageId: number | null;
}
export interface AccountingPackageNodeData {
  name: string | null;
}
export interface PaymentTypeNodeData {
  name: string | null;
  isCC: boolean | null;
  isACH: boolean | null;
  isDefaultForPackage: boolean | null;
  accountingPackageId: number | null;
  ccGatewayId: number | null;
}
export interface GatewayNodeData {
  name: string | null;
  gatewayTypeId: number | null;
  gatewayTypeName: string | null;
  isSandbox: boolean | null;
}
export interface MoreNodeData {
  count: number;
}

/** Broken-link flags; each is present only when the server reported it strictly true. */
export interface GraphNodeFlags {
  isMissing?: true;
  noDefaultPaymentType?: true;
  noPackage?: true;
}

export interface GraphNode {
  key: string;
  type: GraphNodeType;
  id: number;
  label: string;
  data: ItemNodeData | AccountingPackageNodeData | PaymentTypeNodeData | GatewayNodeData | MoreNodeData;
  flags?: GraphNodeFlags;
}
export interface GraphEdge {
  key: string;
  source: string;
  target: string;
  kind: GraphEdgeKind;
}
export interface GatewayResolutionGraph {
  start: string | null;
  nodes: GraphNode[];
  edges: GraphEdge[];
  defaultPath: string[];
}

export const GRAPH_PATH = 'financial/v2/gateway-resolution/graph';
export const DEFAULT_MAX_ITEMS = 50;
export const MORE_STEP = 50;
export const MAX_ITEMS_CAP = 200;
export const START_QUERY_PARAMS: Record<StartType | 'invoice', string> = {
  item: 'itemNum',
  accountingPackage: 'accountingPackageId',
  paymentType: 'paymentTypeId',
  gateway: 'ccGatewayId',
  invoice: 'invoiceNum',
};
export const NODE_DATA_KEYS: Record<GraphNodeType, string[]> = {
  item: ['description', 'accountingPackageId'],
  accountingPackage: ['name'],
  paymentType: ['name', 'isCC', 'isACH', 'isDefaultForPackage', 'accountingPackageId', 'ccGatewayId'],
  gateway: ['name', 'gatewayTypeId', 'gatewayTypeName', 'isSandbox'],
  more: ['count'],
};

const GATEWAY_TYPES = initGatewayTypes();

export function nodeKey(type: GraphNodeType, id: number): string {
  return `${type}:${id}`;
}

function squash(raw: unknown): string | null {
  return typeof raw === 'string' ? raw.toLowerCase().replace(/[-_]/g, '') : null;
}

export function normalizeType(raw: unknown): GraphNodeType | null {
  switch (squash(raw)) {
    case 'item':
      return 'item';
    case 'accountingpackage':
      return 'accountingPackage';
    case 'paymenttype':
      return 'paymentType';
    case 'gateway':
    case 'ccgateway':
      return 'gateway';
    case 'more':
      return 'more';
    default:
      return null;
  }
}

export function normalizeKind(raw: unknown): GraphEdgeKind | null {
  switch (squash(raw)) {
    case 'belongsto':
      return 'belongsTo';
    case 'contains':
      return 'contains';
    case 'uses':
      return 'uses';
    case 'paidwith':
      return 'paidWith';
    case 'defaultsto':
      return 'defaultsTo';
    default:
      return null;
  }
}

export function resolveStart(inputs: {
  itemNum?: number | null;
  accountingPackageId?: number | null;
  paymentTypeId?: number | null;
  ccGatewayId?: number | null;
  invoiceNum?: number | null;
}): GraphStart | null {
  const candidates: [StartType | 'invoice', number | null | undefined][] = [
    ['item', inputs.itemNum],
    ['accountingPackage', inputs.accountingPackageId],
    ['paymentType', inputs.paymentTypeId],
    ['gateway', inputs.ccGatewayId],
    ['invoice', inputs.invoiceNum],
  ];
  for (const [type, id] of candidates) {
    if (typeof id === 'number' && id > 0) {
      return { type, id };
    }
  }
  return null;
}

export function buildGraphPath(start: GraphStart, maxItems?: number | null): string {
  const path = `${GRAPH_PATH}?${START_QUERY_PARAMS[start.type]}=${start.id}`;
  return typeof maxItems === 'number' ? `${path}&maxItems=${maxItems}` : path;
}

export function nextMaxItems(current: number | null | undefined): number | null {
  const base = current ?? DEFAULT_MAX_ITEMS;
  if (base >= MAX_ITEMS_CAP) {
    return null;
  }
  return Math.min(base + MORE_STEP, MAX_ITEMS_CAP);
}

type Wire = Record<string, unknown>;

function isObject(raw: unknown): raw is Wire {
  return raw !== null && typeof raw === 'object' && !Array.isArray(raw);
}

function pick(o: Wire, pascal: string, camel: string): unknown {
  return o[pascal] ?? o[camel];
}

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' ? v : null;
}

function bool(v: unknown): boolean | null {
  return typeof v === 'boolean' ? v : null;
}

function readFlags(w: Wire): GraphNodeFlags | null {
  const flags: GraphNodeFlags = {};
  if (pick(w, 'IsMissing', 'isMissing') === true) {
    flags.isMissing = true;
  }
  if (pick(w, 'NoDefaultPaymentType', 'noDefaultPaymentType') === true) {
    flags.noDefaultPaymentType = true;
  }
  if (pick(w, 'NoPackage', 'noPackage') === true) {
    flags.noPackage = true;
  }
  return Object.keys(flags).length > 0 ? flags : null;
}

function nonEmpty(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

const FALLBACK_LABEL: Record<GraphNodeType, string> = {
  item: 'Item',
  accountingPackage: 'Package',
  paymentType: 'Payment Type',
  gateway: 'Gateway',
  more: 'More',
};

function endpointKey(e: Wire, side: 'Source' | 'Target'): string | null {
  const lower = side === 'Source' ? 'source' : 'target';
  const type = normalizeType(pick(e, `${side}Type`, `${lower}Type`));
  const id = pick(e, `${side}Id`, `${lower}Id`);
  return type !== null && typeof id === 'number' ? nodeKey(type, id) : null;
}

/** The numeric id at the other end of the first kept edge of `kind` joining `key` to a node of `otherType`. */
function derivedId(
  edges: GraphEdge[],
  key: string,
  kind: GraphEdgeKind,
  otherType: GraphNodeType,
): number | null {
  for (const e of edges) {
    if (e.kind !== kind) {
      continue;
    }
    const other = e.source === key ? e.target : e.target === key ? e.source : null;
    if (other !== null && other.startsWith(`${otherType}:`)) {
      const id = Number(other.substring(otherType.length + 1));
      return Number.isFinite(id) ? id : null;
    }
  }
  return null;
}

export function normalizeGraph(raw: unknown): GatewayResolutionGraph | null {
  if (!isObject(raw)) {
    return null;
  }
  const rawNodes = pick(raw, 'Nodes', 'nodes');
  if (!Array.isArray(rawNodes)) {
    return null;
  }
  const rawEdges = pick(raw, 'Edges', 'edges');
  const rawDefault = pick(raw, 'DefaultResolution', 'defaultResolution');

  // Pass 1: nodes (without derived fields).
  const built: { key: string; type: GraphNodeType; id: number; w: Wire; label: string }[] = [];
  const seen = new Set<string>();
  for (const n of rawNodes) {
    if (!isObject(n)) {
      continue;
    }
    const type = normalizeType(pick(n, 'Type', 'type'));
    const id = pick(n, 'Id', 'id');
    if (type === null || typeof id !== 'number') {
      continue;
    }
    const key = nodeKey(type, id);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    let label: string;
    if (type === 'more') {
      const count = num(pick(n, 'Count', 'count')) ?? 0;
      label = `+${count}`;
    } else {
      label =
        nonEmpty(pick(n, 'Label', 'label')) ??
        nonEmpty(pick(n, 'Name', 'name')) ??
        nonEmpty(pick(n, 'Description', 'description')) ??
        `${FALLBACK_LABEL[type]} #${id}`;
    }
    built.push({ key, type, id, w: n, label });
  }

  // Edges.
  const edges: GraphEdge[] = [];
  const edgeKeys = new Set<string>();
  if (Array.isArray(rawEdges)) {
    for (const e of rawEdges) {
      if (!isObject(e)) {
        continue;
      }
      const kind = normalizeKind(pick(e, 'Kind', 'kind'));
      const source = endpointKey(e, 'Source');
      const target = endpointKey(e, 'Target');
      if (kind === null || source === null || target === null || source === target) {
        continue;
      }
      if (!seen.has(source) || !seen.has(target)) {
        continue;
      }
      const key = `${source}|${target}`;
      if (edgeKeys.has(key) || edgeKeys.has(`${target}|${source}`)) {
        continue;
      }
      edgeKeys.add(key);
      edges.push({ key, source, target, kind });
    }
  }

  // Pass 2: allow-listed data literals.
  const nodes: GraphNode[] = built.map(({ key, type, id, w, label }) => {
    let data: GraphNode['data'];
    switch (type) {
      case 'item':
        data = {
          description: str(pick(w, 'Description', 'description')),
          accountingPackageId: derivedId(edges, key, 'belongsTo', 'accountingPackage'),
        };
        break;
      case 'accountingPackage':
        data = { name: str(pick(w, 'Name', 'name')) ?? str(pick(w, 'Label', 'label')) };
        break;
      case 'paymentType':
        data = {
          name: str(pick(w, 'Name', 'name')) ?? str(pick(w, 'Label', 'label')),
          isCC: bool(pick(w, 'IsCreditCard', 'isCreditCard')),
          isACH: bool(pick(w, 'IsAch', 'isAch')),
          isDefaultForPackage: bool(pick(w, 'IsDefaultForPackage', 'isDefaultForPackage')),
          accountingPackageId: derivedId(edges, key, 'contains', 'accountingPackage'),
          ccGatewayId: derivedId(edges, key, 'uses', 'gateway'),
        };
        break;
      case 'gateway': {
        const typeId = num(pick(w, 'GatewayTypeId', 'gatewayTypeId'));
        data = {
          name: str(pick(w, 'Name', 'name')) ?? str(pick(w, 'Label', 'label')),
          gatewayTypeId: typeId,
          gatewayTypeName: GATEWAY_TYPES.find((t) => t.GatewayTypeId === typeId)?.GatewayTypeName ?? null,
          isSandbox: bool(pick(w, 'IsSandbox', 'isSandbox')),
        };
        break;
      }
      default:
        data = { count: num(pick(w, 'Count', 'count')) ?? 0 };
        break;
    }
    const flags = type === 'more' ? null : readFlags(w);
    return flags ? { key, type, id, label, data, flags } : { key, type, id, label, data };
  });

  const rawStart = pick(raw, 'Start', 'start');
  let start: string | null = null;
  if (isObject(rawStart)) {
    const t = normalizeType(pick(rawStart, 'Type', 'type'));
    const i = pick(rawStart, 'Id', 'id');
    if (t !== null && typeof i === 'number' && seen.has(nodeKey(t, i))) {
      start = nodeKey(t, i);
    }
  }

  const defaultPath: string[] = [];
  if (Array.isArray(rawDefault)) {
    for (const d of rawDefault) {
      if (!isObject(d)) {
        continue;
      }
      const t = normalizeType(pick(d, 'Type', 'type'));
      const i = pick(d, 'Id', 'id');
      if (t !== null && typeof i === 'number' && seen.has(nodeKey(t, i))) {
        defaultPath.push(nodeKey(t, i));
      }
    }
  }

  return { start, nodes, edges, defaultPath };
}

/**
 * Keys of the seed nodes (the invoice's line items) the server flagged, in input order, without
 * duplicates, keeping only keys of nodes that exist in the normalised graph. Keys only; nothing else is read.
 */
export function normalizeSeedKeys(raw: unknown): string[] {
  try {
    const graph = normalizeGraph(raw);
    if (graph === null || !isObject(raw)) {
      return [];
    }
    const seeds = pick(raw, 'SeedNodes', 'seedNodes');
    if (!Array.isArray(seeds)) {
      return [];
    }
    const existing = new Set(graph.nodes.map((n) => n.key));
    const keys: string[] = [];
    for (const s of seeds) {
      if (!isObject(s)) {
        continue;
      }
      const type = normalizeType(pick(s, 'Type', 'type'));
      const id = pick(s, 'Id', 'id');
      if (type === null || typeof id !== 'number') {
        continue;
      }
      const key = nodeKey(type, id);
      if (existing.has(key) && !keys.includes(key)) {
        keys.push(key);
      }
    }
    return keys;
  } catch {
    return [];
  }
}

/**
 * The invoice's default paths (one per unpaid line item) as ordered key lists, keeping only keys of
 * nodes that exist in the normalised graph and dropping paths with fewer than two keys. Only Type and Id are read.
 */
export function normalizeDefaultPaths(raw: unknown): string[][] {
  try {
    const graph = normalizeGraph(raw);
    if (graph === null || !isObject(raw)) {
      return [];
    }
    const paths = pick(raw, 'DefaultPaths', 'defaultPaths');
    if (!Array.isArray(paths)) {
      return [];
    }
    const existing = new Set(graph.nodes.map((n) => n.key));
    const result: string[][] = [];
    for (const p of paths) {
      if (!isObject(p)) {
        continue;
      }
      const nodes = pick(p, 'Nodes', 'nodes');
      if (!Array.isArray(nodes)) {
        continue;
      }
      const keys: string[] = [];
      for (const n of nodes) {
        if (!isObject(n)) {
          continue;
        }
        const type = normalizeType(pick(n, 'Type', 'type'));
        const id = pick(n, 'Id', 'id');
        if (type === null || typeof id !== 'number') {
          continue;
        }
        const key = nodeKey(type, id);
        if (existing.has(key)) {
          keys.push(key);
        }
      }
      if (keys.length >= 2) {
        result.push(keys);
      }
    }
    return result;
  } catch {
    return [];
  }
}

export interface GatewayDetailExtras {
  ccGatewayId: number;
  precznMerchantId: string | null;
  precznECheck: boolean | null;
  cardConnectMerchId: string | null;
}

/** Reads only the four allow-listed fields from a legacy CreditCardGateways record. */
export function toGatewayExtras(raw: unknown): GatewayDetailExtras | null {
  if (!isObject(raw)) {
    return null;
  }
  const id = raw['CCGatewayID'] ?? raw['CcGatewayId'];
  if (typeof id !== 'number') {
    return null;
  }
  return {
    ccGatewayId: id,
    precznMerchantId: str(raw['PrecznMerchantId']),
    precznECheck: bool(raw['PrecznECheck']),
    cardConnectMerchId: str(raw['CardConnectMerchId']),
  };
}
